import type { SyncBlockEventPayload } from '@atlaskit/editor-common/analytics';
import type { Experience } from '@atlaskit/editor-common/experiences';
import { logException } from '@atlaskit/editor-common/monitoring';
import type { ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { Node as PMNode, Fragment } from '@atlaskit/editor-prosemirror/model';

import { SyncBlockError } from '../common/types';
import type {
	ResourceId,
	SyncBlockAttrs,
	SyncBlockData as Data,
	SyncBlockNode,
	BlockInstanceId,
	DeletionReason,
	DeletionMechanism,
	ReferenceSyncBlockData,
	SyncBlockStatus,
} from '../common/types';
import type { SyncBlockDataProviderInterface, SyncBlockSourceInfo } from '../providers/types';
import {
	updateErrorPayload,
	createErrorPayload,
	deleteErrorPayload,
	updateCacheErrorPayload,
	getSourceInfoErrorPayload,
	updateSuccessPayload,
	createSuccessPayload,
	createSuccessOperationalPayload,
	addContentSuccessPayload,
	deleteSuccessPayload,
	fetchReferencesErrorPayload,
	buildErrorAttribution,
} from '../utils/errorHandling';
import type { CreateSuccessEnrichment, DeleteSuccessEnrichment } from '../utils/errorHandling';
import {
	getCreateSourceExperience,
	getDeleteSourceExperience,
	getSaveSourceExperience,
	getFetchSourceInfoExperience,
} from '../utils/experienceTracking';
import {
	convertSyncBlockPMNodeToSyncBlockData,
	getSourceProductFromResourceIdSafe,
} from '../utils/utils';

export type ConfirmationCallback = (
	syncBlockIds: SyncBlockAttrs[],
	deleteReason: DeletionReason | undefined,
) => Promise<boolean>;
type OnDelete = () => void;
type OnCompletion = (success: boolean) => void;
type DestroyCallback = () => void;
type SyncBlockData = Data & {
	/**
	 * Cached PM Fragment reference for fast equality comparison via Fragment.eq()
	 * Used for tracking content changes
	 */
	contentFragment?: Fragment;
	/**
	 * Whether the current changes have already been saved to the backend
	 * Defaults to true, so we always flush data on the first save
	 */
	isDirty: boolean;
	/**
	 * Whether the block is waiting to be deleted in backend
	 */
	pendingDeletion?: boolean;
};

/** Maximum time (ms) flush() will wait for in-flight block creations before proceeding. */
const FLUSH_CREATION_AWAIT_TIMEOUT_MS = 1000;

/**
 * Window (ms) within which a repeated `syncedBlockDelete` emission for the same
 * resourceId is suppressed. The same logical removal can re-fire across
 * rebased/remote/redo transactions; a short window collapses those bursts to one
 * event while still allowing a genuine delete→recreate→delete later.
 */
const DELETE_DEDUPE_WINDOW_MS = 5000;

// A store manager responsible for the lifecycle and state management of source sync blocks in an editor instance.
// Designed to manage local in-memory state and synchronize with an external data provider.
// Supports create, flush, and delete operations for source sync blocks.
// Handles caching, debouncing updates, and publish/subscribe for local changes.
// Ensures consistency between local and remote state, and can be used in both editor and renderer contexts.
export class SourceSyncBlockStoreManager {
	private viewMode?: ViewMode;
	private isLivePage?: boolean;
	private dataProvider?: SyncBlockDataProviderInterface;

	private fireAnalyticsEvent?: (payload: SyncBlockEventPayload) => void;

	private syncBlockCache: Map<ResourceId, SyncBlockData>;
	private hasReceivedContentChange: boolean = false;

	private confirmationCallback?: ConfirmationCallback;
	private deletionRetryInfo?: {
		deletionReason: DeletionReason;
		destroyCallback: DestroyCallback;
		mechanism?: DeletionMechanism;
		onDelete: OnDelete;
		onDeleteCompleted: OnCompletion;
		syncBlockIds: SyncBlockAttrs[];
	};

	private creationCompletionCallbacks: Map<ResourceId, OnCompletion>;
	private flushCompletionCallback?: () => void;
	private postCreationFlushCallback?: () => void;
	/**
	 * Promises for in-flight block creations, keyed by resourceId.
	 * `flush()` awaits these so that blocks created from existing content are
	 * persisted even if no further edits trigger a subsequent flush.
	 * See EDITOR-7112.
	 */
	private pendingCreationPromises: Map<ResourceId, Promise<void>> = new Map();
	/**
	 * Set of resource IDs whose creation was still in-flight when a `flush()`
	 * timed out. Each completion (success or failure) is removed from this set;
	 * the first successful completion of any of these IDs triggers
	 * `postCreationFlushCallback` so the content is eventually persisted.
	 * Tracking IDs (not just a boolean) avoids dropping late completions when
	 * multiple blocks are created concurrently. See EDITOR-7112.
	 */
	private creationsTimedOutDuringFlush: Set<ResourceId> = new Set();

	/**
	 * Creation-type signals captured at `createBodiedSyncBlockNode` time and read
	 * back in `commitPendingCreation` to enrich the `syncedBlockCreate` event.
	 * Kept out of the flushed cache record so it never leaks into Block Service /
	 * ADF; removed once consumed.
	 */
	private creationEnrichment: Map<ResourceId, CreateSuccessEnrichment> = new Map();
	/**
	 * Source blocks created by the local insert command in this editor session that
	 * have not yet been reconciled with authoritative reference data. This state is
	 * intentionally kept out of ADF and the backend cache so reloaded, restored,
	 * copied, and remotely inserted sources cannot inherit it.
	 */
	private newSourceBlockResourceIds: Set<ResourceId> = new Set();
	/**
	 * Resource IDs of blocks created empty this session that have not yet fired
	 * the first-content-added event. Added on empty creation, removed when the
	 * block first gains content (the event fires once). Blocks created with
	 * content or not created this session are never tracked.
	 */
	private awaitingFirstContent: Set<ResourceId> = new Set();

	private createExperience: Experience | undefined;
	private saveExperience: Experience | undefined;
	private deleteExperience: Experience | undefined;
	private fetchSourceInfoExperience: Experience | undefined;

	/**
	 * resourceId -> timestamp (ms) of the last `syncedBlockDelete` emission, used
	 * to suppress duplicates within {@link DELETE_DEDUPE_WINDOW_MS}.
	 */
	private recentDeleteEmissions: Map<ResourceId, number> = new Map();

	constructor(
		dataProvider?: SyncBlockDataProviderInterface,
		viewMode?: ViewMode,
		isLivePage?: boolean,
	) {
		this.dataProvider = dataProvider;
		this.viewMode = viewMode;
		this.isLivePage = isLivePage;
		this.syncBlockCache = new Map();
		this.creationCompletionCallbacks = new Map();
	}

	/**
	 * Register a callback to be invoked after flush() completes.
	 * Used by the pm-plugin to dispatch a transaction so that
	 * hasUnsavedBodiedSyncBlockChanges is recalculated in plugin state.
	 */
	public registerFlushCompletionCallback(callback: () => void): void {
		this.flushCompletionCallback = callback;
	}

	/**
	 * Register a callback to be invoked when flush() timed out waiting for a
	 * pending block creation and that creation subsequently completes. The
	 * callback should trigger a deferred flush to persist the content that
	 * was skipped due to the timeout. See EDITOR-7112.
	 */
	public registerPostCreationFlushCallback(callback: () => void): void {
		this.postCreationFlushCallback = callback;
	}

	public setFireAnalyticsEvent(
		fireAnalyticsEvent?: (payload: SyncBlockEventPayload) => void,
	): void {
		this.fireAnalyticsEvent = fireAnalyticsEvent;

		this.createExperience = getCreateSourceExperience(fireAnalyticsEvent);
		this.saveExperience = getSaveSourceExperience(fireAnalyticsEvent);
		this.deleteExperience = getDeleteSourceExperience(fireAnalyticsEvent);
		this.fetchSourceInfoExperience = getFetchSourceInfoExperience(fireAnalyticsEvent);
	}

	public isSourceBlock(node: PMNode): boolean {
		return node.type.name === 'bodiedSyncBlock';
	}

	public isNewSourceBlock(resourceId: ResourceId): boolean {
		return this.newSourceBlockResourceIds.has(resourceId);
	}

	public clearNewSourceBlock(resourceId: ResourceId): void {
		this.newSourceBlockResourceIds.delete(resourceId);
	}

	/**
	 * Add/update a sync block node to/from the local cache
	 * @param syncBlockNode - The sync block node to update
	 */
	public updateSyncBlockData(syncBlockNode: PMNode, isRemote: boolean): boolean {
		try {
			if (this.viewMode === 'view') {
				return false;
			}

			if (!this.isSourceBlock(syncBlockNode)) {
				throw new Error('Invalid sync block node type provided for updateSyncBlockData');
			}

			const { localId, resourceId } = syncBlockNode.attrs;

			if (!localId || !resourceId) {
				// Identifiers not populated yet: benign timing case, skip as a no-op.
				return false;
			}

			const cachedBlock = this.syncBlockCache.get(resourceId);

			// Fast path: if the PM content fragment hasn't changed, skip serialization entirely
			// Fragment.eq() leverages ProseMirror's structural sharing for O(1) comparison
			if (cachedBlock?.contentFragment?.eq(syncBlockNode.content)) {
				return true;
			}

			const syncBlockData = convertSyncBlockPMNodeToSyncBlockData(syncBlockNode);

			if (cachedBlock && !isRemote) {
				this.hasReceivedContentChange = true;
			}

			const isDirty = !isRemote || !cachedBlock; // if the change is not remote, or the block is not in the cache yet, it's dirty
			this.syncBlockCache.set(resourceId, {
				...syncBlockData,
				isDirty: isDirty, // if the change is from remote, it's not dirty
				contentFragment: syncBlockNode.content,
				status: cachedBlock?.status,
			});

			return true;
		} catch (error) {
			logException(error as Error, {
				location: 'editor-synced-block-provider/sourceSyncBlockStoreManager',
			});
			// We can derive the product from `syncBlockNode.attrs.resourceId` even though
			// the variable wasn't destructured (the destructuring step itself may have thrown).
			this.fireAnalyticsEvent?.(
				updateCacheErrorPayload(
					(error as Error).message,
					syncBlockNode?.attrs?.resourceId,
					getSourceProductFromResourceIdSafe(syncBlockNode?.attrs?.resourceId),
				),
			);
			return false;
		}
	}

	/**
	 * Save content of bodiedSyncBlock nodes in local cache to backend
	 *
	 * @returns true if saving all nodes successfully, false if fail to save some/all nodes
	 */
	public async flush(): Promise<boolean> {
		try {
			if (this.viewMode === 'view') {
				return true;
			}

			// Wait (up to FLUSH_CREATION_AWAIT_TIMEOUT_MS) for any in-flight
			// block creations to complete before iterating the cache. Without
			// this, blocks created from existing content are skipped
			// (isPendingCreation check below) and if no further edits are made,
			// no subsequent flush is triggered — causing references to show an
			// "unpublished" error.
			// If the timeout expires, any block whose creation is still
			// in-flight is recorded in `creationsTimedOutDuringFlush` so that
			// `commitPendingCreation()` can trigger a follow-up flush once that
			// specific creation completes.
			// See EDITOR-7112.
			if (this.pendingCreationPromises.size > 0) {
				let timedOut = false;
				let timeoutId: ReturnType<typeof setTimeout> | undefined;
				const timeout = new Promise<void>((resolve) => {
					timeoutId = setTimeout(() => {
						timedOut = true;
						resolve();
					}, FLUSH_CREATION_AWAIT_TIMEOUT_MS);
				});
				await Promise.race([Promise.all(this.pendingCreationPromises.values()), timeout]);
				if (timeoutId !== undefined) {
					clearTimeout(timeoutId);
				}
				if (timedOut) {
					// Record every still-in-flight creation so each late
					// completion is tracked independently.
					for (const resourceId of this.pendingCreationPromises.keys()) {
						this.creationsTimedOutDuringFlush.add(resourceId);
					}
				}
			}

			const bodiedSyncBlockNodes: SyncBlockNode[] = [];
			const bodiedSyncBlockData: SyncBlockData[] = [];

			Array.from(this.syncBlockCache.values()).forEach((syncBlockData) => {
				// Don't flush nodes that
				// - are waiting to be deleted to avoid nodes being re-created
				// - haven't been updated since we last flushed
				// - are still pending BE creation
				if (
					!syncBlockData.pendingDeletion &&
					syncBlockData.isDirty &&
					!this.isPendingCreation(syncBlockData.resourceId)
				) {
					bodiedSyncBlockNodes.push({
						type: 'bodiedSyncBlock',
						attrs: {
							localId: syncBlockData.blockInstanceId,
							resourceId: syncBlockData.resourceId,
						},
					});
					// reset isDirty early to prevent race condition
					// There is a race condition where if a user makes changes to a source sync block
					// on a live page and the source sync block is being saved while the user
					// is still making changes, the new changes might not be saved if they all happen
					// exactly at a time when the writeNodesData is being executed asynchronously.
					syncBlockData.isDirty = false;
					// When flushing, set status to 'active' so the block is published
					const dataToFlush = { ...syncBlockData, status: 'active' as const };
					bodiedSyncBlockData.push(dataToFlush);
				}
			});

			if (bodiedSyncBlockNodes.length === 0) {
				return Promise.resolve(true);
			}

			if (!this.dataProvider) {
				throw new Error('Data provider not set');
			}

			this.saveExperience?.start({});

			const writeResults = await this.dataProvider.writeNodesData(
				bodiedSyncBlockNodes,
				bodiedSyncBlockData,
			);

			writeResults.forEach((result) => {
				// set isDirty to true for cases where it failed to save the sync block to the BE
				if (result.resourceId && result.error && result.error !== SyncBlockError.NotFound) {
					const cachedData = this.syncBlockCache.get(result.resourceId);
					if (cachedData) {
						cachedData.isDirty = true;
					}
				}
			});

			if (writeResults.every((result) => result.resourceId && !result.error)) {
				this.saveExperience?.success();
				writeResults.forEach((result) => {
					if (result.resourceId && !result.error) {
						// Update cache with the status returned from the backend
						const cachedData = this.syncBlockCache.get(result.resourceId);
						if (cachedData && result.status) {
							cachedData.status = result.status;
						}
						this.fireAnalyticsEvent?.(
							updateSuccessPayload(
								result.resourceId,
								false,
								getSourceProductFromResourceIdSafe(result.resourceId),
							),
						);
					}
				});
				return true;
			} else {
				this.saveExperience?.failure();
				writeResults
					.filter((result) => !result.resourceId || result.error)
					.forEach((result) => {
						this.fireAnalyticsEvent?.(
							updateErrorPayload(
								result.error || 'Failed to write data',
								result.resourceId,
								getSourceProductFromResourceIdSafe(result.resourceId),
								buildErrorAttribution(result.error, result.statusCode),
							),
						);
					});

				return false;
			}
		} catch (error) {
			logException(error as Error, {
				location: 'editor-synced-block-provider/sourceSyncBlockStoreManager',
			});
			// Top-level flush failure is not tied to a single resourceId. There is no structured
			// SyncBlockError/status here, so the attribution `reason` falls back to `unknown`.
			this.fireAnalyticsEvent?.(
				updateErrorPayload((error as Error).message, undefined, undefined, buildErrorAttribution()),
			);

			return false;
		} finally {
			this.flushCompletionCallback?.();
		}
	}

	public hasUnsavedChanges(): boolean {
		if (this.viewMode === 'view') {
			return false;
		}

		// Only track unsaved changes in source synced block for live pages
		// classic pages's draft don't publish synced block content to block service and the content itself is saved as part of the draft
		// classic page's publish flow will trigger a flush which only uses the isDirty flag to determine if there are unsaved changes
		if (!this.isLivePage) {
			return false;
		}

		return (
			this.hasReceivedContentChange &&
			Array.from(this.syncBlockCache.values()).some((syncBlockData) => syncBlockData.isDirty)
		);
	}

	public isPendingCreation(resourceId: ResourceId): boolean {
		return this.creationCompletionCallbacks.has(resourceId);
	}

	/**
	 * Returns `true` when at least one sync block is waiting for its backend
	 * creation call to complete. Used as a cheap pre-check in the decoration
	 * rebuild path to avoid a full `doc.descendants()` walk when no creations
	 * are in flight (see EDITOR-6930).
	 */
	public hasPendingCreations(): boolean {
		return this.creationCompletionCallbacks.size > 0;
	}

	/** Returns the source block's last known backend publication status. */
	public getStatus(resourceId: ResourceId): SyncBlockStatus | undefined {
		return this.syncBlockCache.get(resourceId)?.status;
	}

	/**
	 *  Fires callback to insert node (if creation is successful) and clears pending creation data
	 * @param success
	 */
	public commitPendingCreation(success: boolean, resourceId: ResourceId): void {
		if (this.viewMode === 'view') {
			return;
		}

		const onCompletion = this.creationCompletionCallbacks.get(resourceId);
		if (onCompletion) {
			this.creationCompletionCallbacks.delete(resourceId);
			onCompletion(success);
			// If a previous flush() timed out waiting for this specific
			// creation, drop it from the tracking set regardless of outcome so
			// it does not leak. See EDITOR-7112.
			const wasTimedOut = this.creationsTimedOutDuringFlush.delete(resourceId);
			if (success) {
				// If creation is successful, set hasReceivedContentChange to true
				// to indicate that there are unsaved changes in the cache
				this.hasReceivedContentChange = true;
				// If flush() timed out waiting for this creation, notify the
				// plugin so it can trigger a deferred flush. See EDITOR-7112.
				if (wasTimedOut) {
					this.postCreationFlushCallback?.();
				}
			}
		} else {
			this.fireAnalyticsEvent?.(
				createErrorPayload(
					'creation complete callback missing',
					resourceId,
					getSourceProductFromResourceIdSafe(resourceId),
				),
			);
		}

		if (success) {
			const sourceProduct = getSourceProductFromResourceIdSafe(resourceId);
			this.fireAnalyticsEvent?.(createSuccessPayload(resourceId || '', sourceProduct));
			// Operational create-success event with the join key + creation-type
			// signals (inputMethod / createdEmpty) captured at the command layer.
			this.fireAnalyticsEvent?.(
				createSuccessOperationalPayload(
					resourceId || '',
					this.syncBlockCache.get(resourceId)?.blockInstanceId,
					sourceProduct,
					this.creationEnrichment.get(resourceId),
				),
			);
		} else {
			// Delete the node from cache if fail to create so it's not flushed to BE
			this.syncBlockCache.delete(resourceId || '');
			this.newSourceBlockResourceIds.delete(resourceId || '');
			// Creation failed, so there is no block to add content to — drop the
			// first-content tracking entry so a later unrelated edit cannot fire it.
			this.awaitingFirstContent.delete(resourceId || '');
			this.fireAnalyticsEvent?.(
				createErrorPayload(
					'Fail to create bodied sync block',
					resourceId,
					getSourceProductFromResourceIdSafe(resourceId),
				),
			);
		}
		// Enrichment is single-use — drop it once the create has resolved either way.
		this.creationEnrichment.delete(resourceId || '');
	}

	/**
	 * Fire the first-content-added event once when a block created empty this
	 * session first gains content. The caller detects the in-block edit; this owns
	 * dedupe + emission (fires at most once per block, only for empty→content).
	 */
	public maybeEmitFirstContentAdded(
		resourceId: ResourceId,
		blockInstanceId?: BlockInstanceId,
	): void {
		if (this.viewMode === 'view') {
			return;
		}
		// `delete` returns false when the id was not tracked (already emitted, or
		// the block was not created empty this session), so this both dedupes and
		// scopes emission to genuine first-content transitions in one check.
		if (!this.awaitingFirstContent.delete(resourceId)) {
			return;
		}
		this.fireAnalyticsEvent?.(
			addContentSuccessPayload(
				resourceId,
				blockInstanceId,
				getSourceProductFromResourceIdSafe(resourceId),
			),
		);
	}

	public registerConfirmationCallback(callback: ConfirmationCallback) {
		this.confirmationCallback = callback;

		return (): void => {
			this.confirmationCallback = undefined;
		};
	}

	public requireConfirmationBeforeDelete(): boolean {
		return !!this.confirmationCallback;
	}

	/**
	 * @returns attributes for a new bodiedSyncBlock node
	 */
	public generateBodiedSyncBlockAttrs(): SyncBlockAttrs {
		if (!this.dataProvider) {
			throw new Error('Data provider not set or source id not set');
		}

		const { resourceId, localId } = this.dataProvider.generateResourceId();
		return { resourceId, localId };
	}

	/**
	 * Create a bodiedSyncBlock node with empty content to backend
	 * @param attrs attributes Ids of the node
	 * @param node the ProseMirror node to cache
	 * @param onCompletion callback invoked when creation completes
	 * @param enrichment optional creation-type signals stashed and attached to the
	 *   `syncedBlockCreate` event when the async create resolves. When
	 *   `createdEmpty` is true the block is also registered for the
	 *   first-content-added event.
	 */
	public createBodiedSyncBlockNode(
		attrs: SyncBlockAttrs,
		node: PMNode,
		onCompletion: OnCompletion,
		enrichment?: CreateSuccessEnrichment,
	): void {
		if (this.viewMode === 'view') {
			return;
		}

		const { resourceId, localId: blockInstanceId } = attrs;
		// Stash creation-type signals for commitPendingCreation to read.
		if (enrichment) {
			this.creationEnrichment.set(resourceId, enrichment);
			// Only empty-created blocks can produce an empty→content transition.
			if (enrichment.createdEmpty) {
				this.awaitingFirstContent.add(resourceId);
			}
		}
		try {
			if (!this.dataProvider) {
				throw new Error('Data provider not set');
			}

			if (enrichment) {
				this.newSourceBlockResourceIds.add(resourceId);
			}

			// add the node to the cache
			this.updateSyncBlockData(node, false);

			// Mark the block as unpublished in the cache so it can be cleaned up on cancel
			const cached = this.syncBlockCache.get(resourceId);
			if (cached) {
				cached.status = 'unpublished';
			}

			this.creationCompletionCallbacks.set(resourceId, onCompletion);
			this.createExperience?.start({});
			const creationPromise = this.dataProvider
				.createNodeData({
					content: [],
					blockInstanceId,
					resourceId,
				})
				.then((result) => {
					const resourceId = result.resourceId || '';
					if (resourceId && !result.error) {
						this.commitPendingCreation(true, resourceId);

						this.createExperience?.success();
					} else {
						this.commitPendingCreation(false, resourceId);
						this.createExperience?.failure({
							reason: result.error || 'Failed to create bodied sync block',
						});
						this.fireAnalyticsEvent?.(
							createErrorPayload(
								result.error || 'Failed to create bodied sync block',
								resourceId,
								getSourceProductFromResourceIdSafe(resourceId),
								buildErrorAttribution(result.error, result.statusCode),
							),
						);
					}
				})
				.catch((error) => {
					this.commitPendingCreation(false, resourceId);
					logException(error as Error, {
						location: 'editor-synced-block-provider/sourceSyncBlockStoreManager',
					});
					this.createExperience?.failure({ reason: (error as Error).message });
					this.fireAnalyticsEvent?.(
						createErrorPayload(
							(error as Error).message,
							resourceId,
							getSourceProductFromResourceIdSafe(resourceId),
						),
					);
				})
				.finally(() => {
					this.pendingCreationPromises.delete(resourceId);
				});
			this.pendingCreationPromises.set(resourceId, creationPromise);
		} catch (error) {
			this.newSourceBlockResourceIds.delete(resourceId);
			if (this.isPendingCreation(resourceId)) {
				this.commitPendingCreation(false, resourceId);
			}
			logException(error as Error, {
				location: 'editor-synced-block-provider/sourceSyncBlockStoreManager',
			});
			this.fireAnalyticsEvent?.(
				createErrorPayload(
					(error as Error).message,
					resourceId,
					getSourceProductFromResourceIdSafe(resourceId),
				),
			);
		}
	}

	private setPendingDeletion = (Ids: SyncBlockAttrs, value: boolean) => {
		if (this.viewMode === 'view') {
			return;
		}

		const syncBlock = this.syncBlockCache.get(Ids.resourceId);
		if (syncBlock) {
			syncBlock.pendingDeletion = value;
		}
	};

	/**
	 * Drop dedupe entries older than {@link DELETE_DEDUPE_WINDOW_MS} so
	 * `recentDeleteEmissions` stays bounded by the number of recent deletes.
	 */
	private pruneRecentDeleteEmissions(now: number): void {
		for (const [resourceId, emittedAt] of this.recentDeleteEmissions) {
			if (now - emittedAt >= DELETE_DEDUPE_WINDOW_MS) {
				this.recentDeleteEmissions.delete(resourceId);
			}
		}
	}

	/**
	 * Emit the `syncedBlockDelete` success event. Gate off: legacy payload. Gate
	 * on: attaches `deletionReason`, `mechanism` and `blockInstanceId`, and
	 * suppresses repeat emissions within {@link DELETE_DEDUPE_WINDOW_MS}. Must run
	 * while the cache entry still exists so `blockInstanceId` is available.
	 */
	private emitDeleteSuccess(
		resourceId: ResourceId,
		reason: DeletionReason,
		mechanism: DeletionMechanism | undefined,
	): void {
		const sourceProduct = getSourceProductFromResourceIdSafe(resourceId);

		const now = Date.now();
		const lastEmittedAt = this.recentDeleteEmissions.get(resourceId);
		if (lastEmittedAt !== undefined && now - lastEmittedAt < DELETE_DEDUPE_WINDOW_MS) {
			return; // duplicate emission for the same logical removal
		}
		this.pruneRecentDeleteEmissions(now);
		this.recentDeleteEmissions.set(resourceId, now);

		const enrichment: DeleteSuccessEnrichment = {
			blockInstanceId: this.syncBlockCache.get(resourceId)?.blockInstanceId,
			deletionReason: reason,
			mechanism,
		};

		this.fireAnalyticsEvent?.(deleteSuccessPayload(resourceId, sourceProduct, enrichment));
	}

	private async delete(
		syncBlockIds: SyncBlockAttrs[],
		onDelete: OnDelete,
		onDeleteCompleted: OnCompletion,
		reason: DeletionReason,
		mechanism?: DeletionMechanism,
	): Promise<boolean> {
		try {
			if (this.viewMode === 'view') {
				return false;
			}

			if (!this.dataProvider) {
				throw new Error('Data provider not set');
			}

			syncBlockIds.forEach((Ids) => {
				this.setPendingDeletion(Ids, true);
			});

			this.deleteExperience?.start({});

			const results = await this.dataProvider.deleteNodesData(
				syncBlockIds.map((attrs) => attrs.resourceId),
				reason,
			);

			let callback;
			const isDeleteSuccessful = results.every((result) => result.success);
			onDeleteCompleted(isDeleteSuccessful);

			if (isDeleteSuccessful) {
				onDelete();
				// Emit before the cache entry is deleted below, so blockInstanceId
				// is still available to emitDeleteSuccess.
				results.forEach((result) => {
					this.emitDeleteSuccess(result.resourceId, reason, mechanism);
				});
				callback = (Ids: SyncBlockAttrs) => this.syncBlockCache.delete(Ids.resourceId);
				this.clearPendingDeletion();
				this.deleteExperience?.success();
			} else {
				callback = (Ids: SyncBlockAttrs) => {
					this.setPendingDeletion(Ids, false);
				};

				this.deleteExperience?.failure();
				results.forEach((result) => {
					if (result.success) {
						this.emitDeleteSuccess(result.resourceId, reason, mechanism);
					} else {
						this.fireAnalyticsEvent?.(
							deleteErrorPayload(
								result.error || 'Failed to delete synced block',
								result.resourceId,
								getSourceProductFromResourceIdSafe(result.resourceId),
								buildErrorAttribution(result.error, result.statusCode),
							),
						);
					}
				});
			}

			syncBlockIds.forEach(callback);
			return isDeleteSuccessful;
		} catch (error) {
			// Thrown (non-result) failure — no structured SyncBlockError/status is available,
			// so the attribution `reason` falls back to `unknown` when the gate is on.
			const attribution = buildErrorAttribution();
			syncBlockIds.forEach((Ids) => {
				this.setPendingDeletion(Ids, false);
				this.fireAnalyticsEvent?.(
					deleteErrorPayload(
						(error as Error).message,
						Ids.resourceId,
						getSourceProductFromResourceIdSafe(Ids.resourceId),
						attribution,
					),
				);
			});
			logException(error as Error, {
				location: 'editor-synced-block-provider/sourceSyncBlockStoreManager',
			});
			onDeleteCompleted(false);
			return false;
		}
	}

	public isRetryingDeletion(): boolean {
		return !!this.deletionRetryInfo;
	}

	public async retryDeletion(): Promise<void> {
		if (this.viewMode === 'view') {
			return Promise.resolve();
		}

		if (!this.deletionRetryInfo) {
			return Promise.resolve();
		}
		const { syncBlockIds, onDelete, onDeleteCompleted, deletionReason, mechanism } =
			this.deletionRetryInfo;

		if (this.confirmationCallback) {
			await this.delete(syncBlockIds, onDelete, onDeleteCompleted, deletionReason, mechanism);
		}
	}

	public clearPendingDeletion(): void {
		this.deletionRetryInfo?.destroyCallback();
		this.deletionRetryInfo = undefined;
	}

	/**
	 * Fetches the current status of all source sync blocks in the cache from the backend
	 * and updates the cache entries with the fetched status.
	 * This is called on editor init so we know which blocks are 'unpublished' vs 'active'.
	 */
	public async fetchAndCacheStatuses(): Promise<void> {
		if (!this.dataProvider || this.syncBlockCache.size === 0) {
			return;
		}

		// Source blocks have plain UUID resourceIds, but fetchNodesData internally uses
		// generateBlockAriFromReference which expects reference-format resourceIds
		// (e.g. "confluence-page/pageId/uuid"). We convert source resourceIds to reference
		// format before fetching, and maintain a mapping back to original resourceIds
		// so we can update the correct cache entries.
		const sourceToReferenceMap = new Map<string, string>();
		const syncBlockNodes: SyncBlockNode[] = Array.from(this.syncBlockCache.entries()).map(
			([resourceId, data]) => {
				const referenceResourceId =
					this.dataProvider?.generateResourceIdForReference(resourceId) ?? resourceId;
				sourceToReferenceMap.set(referenceResourceId, resourceId);
				return {
					type: 'bodiedSyncBlock' as const,
					attrs: {
						localId: data.blockInstanceId,
						resourceId: referenceResourceId,
					},
				};
			},
		);

		try {
			const results = await this.dataProvider.fetchNodesData(syncBlockNodes);
			for (const result of results) {
				// Map the reference resourceId back to the source resourceId
				const sourceResourceId = sourceToReferenceMap.get(result.resourceId) ?? result.resourceId;
				const cached = this.syncBlockCache.get(sourceResourceId);
				if (cached && result.data?.status) {
					cached.status = result.data.status;
				}
			}
		} catch {
			// If the fetch fails, statuses remain undefined.
			// This is acceptable — on cancel, blocks without a known status
			// will not be deleted (safe default).
		}
	}

	/**
	 * Deletes all source sync blocks that have 'unpublished' status.
	 * Used to clean up orphaned blocks when a user cancels editing without saving.
	 * Blocks that were already saved (status 'active') are not affected.
	 *
	 * @returns true if all deletions succeeded, false otherwise
	 */
	public discardUnpublishedBlocks(): Promise<boolean> {
		const unpublishedBlockIds: SyncBlockAttrs[] = Array.from(this.syncBlockCache.entries())
			.filter(([_, data]) => data.status === 'unpublished' && !data.pendingDeletion)
			.map(([resourceId, data]) => ({
				resourceId,
				localId: data.blockInstanceId,
			}));

		if (unpublishedBlockIds.length === 0) {
			return Promise.resolve(true);
		}

		return this.delete(
			unpublishedBlockIds,
			() => {}, // onDelete: no-op, document is being discarded
			() => {}, // onDeleteCompleted: no-op
			'source-block-unpublished',
			// Cancel/discard cleanup is code-initiated, not a direct user edit.
			'other',
		);
	}

	/**
	 * Deletes sync blocks with confirmation from the backend
	 *
	 * @param syncBlockIds - The sync block ids to delete
	 * @param onDelete - The callback to delete sync block node from document
	 * @param onDeleteCompleted - The callback for after the deletion is saved to BE (whether successful or not)
	 * @param destroyCallback - The callback to clear any reference stored for deletion (regardless if deletion is completed or abort)
	 */
	public async deleteSyncBlocksWithConfirmation(
		syncBlockIds: SyncBlockAttrs[],
		deletionReason: DeletionReason,
		onDelete: OnDelete,
		onDeleteCompleted: OnCompletion,
		destroyCallback: DestroyCallback,
		mechanism?: DeletionMechanism,
	): Promise<void> {
		if (this.viewMode === 'view') {
			return Promise.resolve();
		}

		if (this.confirmationCallback) {
			const confirmed = await this.confirmationCallback(syncBlockIds, deletionReason);
			if (confirmed) {
				const isDeleteSuccessful = await this.delete(
					syncBlockIds,
					onDelete,
					onDeleteCompleted,
					deletionReason,
					mechanism,
				);

				if (!isDeleteSuccessful) {
					// If deletion failed, save deletion info for potential retry
					this.deletionRetryInfo = {
						syncBlockIds,
						onDelete,
						onDeleteCompleted,
						destroyCallback,
						deletionReason,
						mechanism,
					};
				} else {
					destroyCallback();
				}
			} else {
				destroyCallback();
			}
		}
	}

	getSyncBlockSourceInfo(localId: BlockInstanceId): Promise<SyncBlockSourceInfo | undefined> {
		try {
			if (!this.dataProvider) {
				throw new Error('Data provider not set');
			}

			this.fetchSourceInfoExperience?.start();
			return this.dataProvider
				.fetchSyncBlockSourceInfo(localId, undefined, undefined)
				.then((sourceInfo) => {
					if (!sourceInfo) {
						this.fetchSourceInfoExperience?.failure({ reason: 'No source info returned' });
					} else {
						this.fetchSourceInfoExperience?.success();
					}

					return sourceInfo;
				});
		} catch (error) {
			logException(error as Error, {
				location: 'editor-synced-block-provider/sourceSyncBlockStoreManager',
			});
			this.fireAnalyticsEvent?.(getSourceInfoErrorPayload((error as Error).message));

			return Promise.resolve(undefined);
		}
	}

	fetchReferences(resourceId: string): Promise<ReferenceSyncBlockData> {
		try {
			if (!this.dataProvider) {
				throw new Error('Data provider not set');
			}

			return this.dataProvider.fetchReferences(resourceId, true);
		} catch (error) {
			logException(error as Error, {
				location: 'editor-synced-block-provider/sourceSyncBlockStoreManager',
			});
			this.fireAnalyticsEvent?.(
				fetchReferencesErrorPayload(
					(error as Error).message,
					resourceId,
					getSourceProductFromResourceIdSafe(resourceId),
				),
			);

			return Promise.resolve({ error: SyncBlockError.Errored });
		}
	}

	public destroy(): void {
		this.syncBlockCache.clear();
		this.confirmationCallback = undefined;
		this.creationCompletionCallbacks.clear();
		this.flushCompletionCallback = undefined;
		this.postCreationFlushCallback = undefined;
		this.pendingCreationPromises.clear();
		this.creationsTimedOutDuringFlush.clear();
		this.creationEnrichment.clear();
		this.newSourceBlockResourceIds.clear();
		this.awaitingFirstContent.clear();
		this.recentDeleteEmissions.clear();
		this.dataProvider = undefined;
		this.saveExperience?.abort({ reason: 'editorDestroyed' });
		this.createExperience?.abort({ reason: 'editorDestroyed' });
		this.deleteExperience?.abort({ reason: 'editorDestroyed' });
		this.fetchSourceInfoExperience?.abort({ reason: 'editorDestroyed' });
		this.clearPendingDeletion();
	}
}
